import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Lightbulb, ChevronRight, ChevronLeft, Scale, 
  Target, Heart, Sparkles, CheckCircle2, Brain,
  RefreshCw, Award
} from 'lucide-react';

interface ReframingStep {
  id: string;
  title: string;
  description: string;
  prompt: string;
  tip: string;
  icon: React.ReactNode;
}

const REFRAMING_STEPS: ReframingStep[] = [
  {
    id: 'original',
    title: 'The Original Thought',
    description: 'Start by clearly stating the thought that\'s troubling you',
    prompt: 'Write down the exact thought as it appeared in your mind...',
    tip: 'Be specific. "I\'m worthless" is better than "bad thoughts". The more precise, the easier to challenge.',
    icon: <Brain className="w-5 h-5" />
  },
  {
    id: 'emotion',
    title: 'Emotional Impact',
    description: 'How does this thought make you feel?',
    prompt: 'What emotions come up when you think this thought? How intense are they?',
    tip: 'Thoughts and emotions are connected but different. The same situation can create different emotions depending on how we interpret it.',
    icon: <Heart className="w-5 h-5" />
  },
  {
    id: 'evidence_for',
    title: 'Evidence Supporting',
    description: 'What evidence supports this thought being true?',
    prompt: 'List any facts (not feelings) that support this thought...',
    tip: 'Focus on objective facts, not interpretations. "My boss frowned" is fact. "My boss hates me" is interpretation.',
    icon: <Scale className="w-5 h-5" />
  },
  {
    id: 'evidence_against',
    title: 'Evidence Against',
    description: 'What evidence contradicts this thought?',
    prompt: 'List any facts that don\'t fit with this thought or contradict it...',
    tip: 'Think of times when the opposite was true. What would a friend say? What are you overlooking?',
    icon: <Scale className="w-5 h-5" />
  },
  {
    id: 'thinking_trap',
    title: 'Thinking Pattern',
    description: 'Can you identify any thinking traps?',
    prompt: 'Which cognitive distortions might be at play here?',
    tip: 'Common traps: All-or-nothing thinking, mind reading, catastrophising, fortune telling, discounting positives.',
    icon: <Target className="w-5 h-5" />
  },
  {
    id: 'alternative',
    title: 'Balanced Alternative',
    description: 'Create a more balanced perspective',
    prompt: 'Based on all the evidence, what\'s a more balanced way to think about this?',
    tip: 'This isn\'t about being positive - it\'s about being accurate. Include the negatives if they\'re real, but also the positives.',
    icon: <Lightbulb className="w-5 h-5" />
  },
  {
    id: 'action',
    title: 'Action Step',
    description: 'What can you do with this new perspective?',
    prompt: 'Is there a small action you could take based on this reframe?',
    tip: 'Even tiny actions build new patterns. Sometimes the action is simply to notice and not react.',
    icon: <Sparkles className="w-5 h-5" />
  }
];

// Reframing prompts to help users
const REFRAMING_HELPERS = [
  'What would I tell a friend in this situation?',
  'What would I think about this in 5 years?',
  'Am I thinking in extremes (all/nothing, always/never)?',
  'What information am I ignoring?',
  'Is this a fact or an interpretation?',
  'What\'s another way to look at this?',
  'What would someone who loves me say?',
  'Am I being compassionate to myself?'
];

interface GuidedReframingExerciseProps {
  initialThought?: string;
  onComplete: (data: ReframingData) => void;
  onCancel?: () => void;
}

export interface ReframingData {
  original_thought: string;
  emotional_impact: string;
  evidence_for: string;
  evidence_against: string;
  thinking_trap: string;
  alternative_thought: string;
  action_step: string;
  completed_at: string;
}

export function GuidedReframingExercise({ 
  initialThought = '', 
  onComplete,
  onCancel 
}: GuidedReframingExerciseProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [responses, setResponses] = useState<Record<string, string>>({
    original: initialThought,
    emotion: '',
    evidence_for: '',
    evidence_against: '',
    thinking_trap: '',
    alternative: '',
    action: ''
  });
  const [showHelper, setShowHelper] = useState(false);
  const [isComplete, setIsComplete] = useState(false);

  const progress = ((currentStep + 1) / REFRAMING_STEPS.length) * 100;
  const step = REFRAMING_STEPS[currentStep];

  const handleNext = () => {
    if (currentStep < REFRAMING_STEPS.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      handleComplete();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleComplete = () => {
    const data: ReframingData = {
      original_thought: responses.original,
      emotional_impact: responses.emotion,
      evidence_for: responses.evidence_for,
      evidence_against: responses.evidence_against,
      thinking_trap: responses.thinking_trap,
      alternative_thought: responses.alternative,
      action_step: responses.action,
      completed_at: new Date().toISOString()
    };
    setIsComplete(true);
    onComplete(data);
  };

  const updateResponse = (value: string) => {
    setResponses(prev => ({
      ...prev,
      [step.id]: value
    }));
  };

  const canProceed = responses[step.id]?.trim().length > 0;

  if (isComplete) {
    return (
      <Card className="border-green-500/30 bg-gradient-to-br from-green-500/5 to-emerald-500/5">
        <CardContent className="pt-6 text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', duration: 0.5 }}
          >
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8 text-green-500" />
            </div>
          </motion.div>
          <h3 className="text-xl font-bold mb-2">Reframe Complete!</h3>
          <p className="text-muted-foreground mb-4">
            Well done for taking the time to work through this thought. 
            This kind of practice builds new neural pathways over time.
          </p>
          
          {/* Summary */}
          <div className="text-left mt-6 space-y-4">
            <div className="p-4 rounded-lg bg-muted/50 border">
              <p className="text-sm font-medium mb-1">Original thought:</p>
              <p className="text-sm text-muted-foreground italic">"{responses.original}"</p>
            </div>
            <div className="p-4 rounded-lg bg-primary/5 border border-primary/20">
              <p className="text-sm font-medium mb-1">Reframed to:</p>
              <p className="text-sm text-primary italic">"{responses.alternative}"</p>
            </div>
            {responses.action && (
              <div className="p-4 rounded-lg bg-accent/5 border border-accent/20">
                <p className="text-sm font-medium mb-1">Action step:</p>
                <p className="text-sm text-accent">{responses.action}</p>
              </div>
            )}
          </div>

          <div className="flex items-center justify-center gap-2 mt-6">
            <Award className="w-5 h-5 text-amber-500" />
            <span className="text-sm text-muted-foreground">+10 XP for completing a reframe</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-primary/20">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between mb-2">
          <Badge variant="outline">Step {currentStep + 1} of {REFRAMING_STEPS.length}</Badge>
          {onCancel && (
            <Button variant="ghost" size="sm" onClick={onCancel}>
              Cancel
            </Button>
          )}
        </div>
        <Progress value={progress} className="h-2" />
      </CardHeader>
      
      <CardContent className="space-y-4">
        <AnimatePresence mode="wait">
          <motion.div
            key={step.id}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
            className="space-y-4"
          >
            {/* Step Header */}
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-full bg-primary/10">
                {step.icon}
              </div>
              <div>
                <h3 className="font-semibold">{step.title}</h3>
                <p className="text-sm text-muted-foreground">{step.description}</p>
              </div>
            </div>

            {/* Input Area */}
            <Textarea
              placeholder={step.prompt}
              value={responses[step.id]}
              onChange={(e) => updateResponse(e.target.value)}
              className="min-h-[120px] border-primary/30 focus:border-primary"
            />

            {/* Tip */}
            <div className="p-3 rounded-lg bg-amber-500/5 border border-amber-500/20">
              <div className="flex items-start gap-2">
                <Lightbulb className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                <p className="text-xs text-muted-foreground">{step.tip}</p>
              </div>
            </div>

            {/* Helper Prompts */}
            {(step.id === 'alternative' || step.id === 'evidence_against') && (
              <div className="space-y-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowHelper(!showHelper)}
                  className="text-xs"
                >
                  <RefreshCw className="w-3 h-3 mr-1" />
                  {showHelper ? 'Hide' : 'Show'} thinking prompts
                </Button>
                
                <AnimatePresence>
                  {showHelper && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="p-3 rounded-lg bg-muted/50 border">
                        <p className="text-xs font-medium mb-2">Ask yourself:</p>
                        <div className="flex flex-wrap gap-1">
                          {REFRAMING_HELPERS.map((helper, i) => (
                            <Badge 
                              key={i} 
                              variant="secondary" 
                              className="text-xs cursor-pointer hover:bg-primary/20"
                              onClick={() => updateResponse(responses[step.id] + (responses[step.id] ? '\n' : '') + helper)}
                            >
                              {helper}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}

            {/* Navigation */}
            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={handleBack}
                disabled={currentStep === 0}
                className="flex-1"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
              <Button
                onClick={handleNext}
                disabled={!canProceed}
                className="flex-1"
              >
                {currentStep === REFRAMING_STEPS.length - 1 ? (
                  <>
                    <CheckCircle2 className="w-4 h-4 mr-1" />
                    Complete
                  </>
                ) : (
                  <>
                    Next
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </>
                )}
              </Button>
            </div>
          </motion.div>
        </AnimatePresence>
      </CardContent>
    </Card>
  );
}
